// Contracts — kids delivery goal + pace-to-deadline (2026-06-29). Goal is PER
// CLUSTER (atlanta / remote) — each cluster has its own packed-sessions target +
// manager-set deadline, so office segmentation holds (Atlanta never sees anything
// derived from Remote). The per-scope plan is derived at query time strictly
// inside the cluster (cluster_target / N moderators in cluster). Shapes snapped
// from migrations/0007_goals.sql (id = cluster slug).
import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const KidsClusterSchema = z.enum(['atlanta', 'remote']);
export type KidsCluster = z.infer<typeof KidsClusterSchema>;

// Pace may additionally be viewed ACROSS clusters (manager only): ?cluster=all →
// combined target (sum of both goals), later of the two deadlines, no cluster
// filter on the counts (Tim 02.07: «общий за оба кластера»). Goals themselves
// stay strictly per-cluster — 'all' is a read-time aggregation, never a goal row.
export const KidsPaceClusterSchema = z.enum(['atlanta', 'remote', 'all']);
export type KidsPaceCluster = z.infer<typeof KidsPaceClusterSchema>;

// GET /api/kids/goal[?cluster=] → the goal config for the caller's cluster.
export const KidsGoalResSchema = z.object({
  cluster: KidsClusterSchema,
  target: z.number(),
  deadline: z.string().nullable(),
});
export type KidsGoalRes = z.infer<typeof KidsGoalResSchema>;

// PUT /api/kids/goal (manager) → set a cluster's deadline and/or target.
export const KidsGoalUpdateReqSchema = z
  .object({
    cluster: KidsClusterSchema,
    deadline: z.string().regex(DATE_RE).nullable().optional(),
    target: z.number().int().positive().max(1_000_000).optional(),
  })
  .refine((d) => d.deadline !== undefined || d.target !== undefined, {
    message: 'nothing to update',
  });
export type KidsGoalUpdateReq = z.infer<typeof KidsGoalUpdateReqSchema>;

// GET /api/kids/pace[?cluster=&office=] → cluster-scoped pace toward the goal.
//   scope: moderator→self, admin→own office, manager→a cluster (?cluster=, +office)
//   cluster: the cluster everything is computed inside; target = the SCOPE quota
//   (moderator quota / office sum / whole-cluster target)
export const KidsPaceDaySchema = z.object({
  date: z.string(), // YYYY-MM-DD
  packed: z.number(),
});

export const KidsPaceResSchema = z.object({
  scope: z.enum(['moderator', 'admin', 'manager']),
  cluster: KidsPaceClusterSchema,
  office: z.string().nullable(),
  target: z.number(), // scope quota
  deadline: z.string().nullable(),
  done: z.number(), // shot (status=done)
  packed: z.number(), // shipped (goal metric)
  accepted: z.number(), // from external tracker — 0 placeholder in V1
  remaining: z.number(), // max(quota - packed, 0)
  daysRemaining: z.number().nullable(), // null when no deadline
  requiredPerDay: z.number().nullable(), // ceil(remaining / daysRemaining)
  recentPerDay: z.number(), // packed last 7 days / 7 (velocity)
  onTrack: z.boolean().nullable(), // projected >= quota by deadline
  moderators: z.number(), // N in scope (context)
  totalModerators: z.number(), // N moderators in the resolved cluster (competition cue)
  daily: z.array(KidsPaceDaySchema), // last 14 days, packed by ended_at date
});
export type KidsPaceRes = z.infer<typeof KidsPaceResSchema>;
